import React, { useEffect, useMemo, useState } from 'react';
import {
  useGetPolicySignaturesQuery,
  useExportPolicySignaturesMutation,
  useNudgePolicyMutation,
} from '../../store/api/localApi';
import { useToast } from '../../components/Toast';
import { formatDublinDateTime } from '../../utils/date';
import type { PolicyCoverage, PolicySignatureRow } from '../../types';
import { coverageAudienceLabel } from '../../components/policies/audienceLabel';
import './policy-responses.print.css';

const BATCH_SIZE = 25;
type View = 'signed' | 'outstanding';

interface Props {
  policyId: string;
  coverage: PolicyCoverage;
}

/**
 * The Responses panel on a policy. Two views — Signed (the signature
 * register: who signed, for which child, when, which version) and Still to
 * confirm — because an inspector asks both questions and the old single
 * unlabelled list of outstanding names read as the opposite of what it was.
 *
 * Counts are over the ACTIVE children the policy is sent to (the backend
 * excludes departed children and, for a policy sent to chosen classes, children
 * outside them); departed children who signed while enrolled are still listed
 * under Signed with a chip, and counted in a footnote, as are children who
 * signed but are no longer in the chosen classes.
 * Rows come in batches of 25 like the attendance register; print and the
 * CSV always carry every row.
 *
 * The version picker re-reads the signature register only. Coverage — and so
 * Still to confirm — is always the current version's, which is why that view
 * is disabled while an older version is picked.
 */
const PolicyResponses: React.FC<Props> = ({ policyId, coverage }) => {
  const { showError } = useToast();
  const [view, setView] = useState<View>('signed');
  const [versionId, setVersionId] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState(BATCH_SIZE);
  const { data: register, isLoading } = useGetPolicySignaturesQuery({ policyId, versionId });
  const [exportCsv, { isLoading: exporting }] = useExportPolicySignaturesMutation();
  const [nudge, { isLoading: nudging }] = useNudgePolicyMutation();
  const [nudgeMessage, setNudgeMessage] = useState<string | null>(null);

  // Rows are ALL rendered — the ones outside the search or beyond the current
  // batch are hidden on screen and shown on paper (`print:table-row`), because
  // the printed register is the whole list whatever the screen was showing.
  const q = search.trim().toLowerCase();
  const allSignedRows = register?.signatures ?? [];
  const matchingSigned = useMemo(
    () =>
      q
        ? allSignedRows.filter((r) =>
            [r.childName, r.userName, r.signedName].some((s) => s?.toLowerCase().includes(q))
          )
        : allSignedRows,
    [allSignedRows, q]
  );
  const allOutstandingRows = coverage.parents.outstandingChildren;
  const matchingOutstanding = useMemo(
    () =>
      q
        ? allOutstandingRows.filter((c) => c.childName.toLowerCase().includes(q))
        : allOutstandingRows,
    [allOutstandingRows, q]
  );

  const shownSignedIds = useMemo(
    () => new Set(matchingSigned.slice(0, visible).map((r) => r.ackId)),
    [matchingSigned, visible]
  );
  const shownOutstandingIds = useMemo(
    () => new Set(matchingOutstanding.slice(0, visible).map((c) => c.childId)),
    [matchingOutstanding, visible]
  );

  const total = view === 'signed' ? matchingSigned.length : matchingOutstanding.length;
  const shown = view === 'signed' ? shownSignedIds.size : shownOutstandingIds.size;

  // Coverage — and so the outstanding list and its count — is always the
  // CURRENT version's; the picker only re-reads the signature register. So
  // "Still to confirm" has no answer for an older version, and offering it
  // would show today's names under last year's heading while the CSV
  // (computed server-side for the picked version) said something else.
  //
  // Anchored on the COVERAGE prop, never on `register.version`: the register
  // endpoint echoes back whatever version was asked for, so comparing the
  // picked id against it is a tautology that is always true once the query
  // settles. PolicyDetail only renders this panel once `coverage.version`
  // exists.
  const currentVersionId = coverage.version?.versionId;
  const isCurrentVersion = !versionId || versionId === currentVersionId;

  // Any change of view, version or search starts again from the first batch.
  useEffect(() => {
    setVisible(BATCH_SIZE);
  }, [view, versionId, search]);

  useEffect(() => {
    if (!isCurrentVersion) setView('signed');
  }, [isCurrentVersion]);

  const handleDownload = async () => {
    try {
      // The outstanding export is only ever asked for the current version,
      // matching the list on screen. Signed follows the picker.
      const { blob, filename } = await exportCsv(
        view === 'signed' ? { policyId, view, versionId } : { policyId, view }
      ).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      // Revoking synchronously can beat the browser's read of the href in
      // Safari, leaving an empty file.
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch {
      showError('Could not download the file. Try again.');
    }
  };

  const handleNudge = async () => {
    try {
      const res = await nudge(policyId).unwrap();
      setNudgeMessage(
        res.outstandingChildren === 0
          ? 'Everyone has responded — nobody to remind.'
          : `Reminder sent for ${res.outstandingChildren} ${
              res.outstandingChildren === 1 ? 'child' : 'children'
            }.`
      );
    } catch (err) {
      // A 429 here means the nudge was rate-limited — a reminder went out
      // recently. Without this branch the admin tapped the button and got
      // zero feedback, so they'd tap again (or assume it silently worked).
      const status = (err as { status?: number })?.status;
      const message =
        status === 429
          ? 'Reminder already sent recently — try again later.'
          : (err as { data?: { error?: string } })?.data?.error || 'Failed to send the reminder.';
      showError(message, 'policy-nudge-error');
    }
  };

  const outstandingCount = coverage.parents.outstandingChildren.length;
  const departedSigned = coverage.parents.departedSigned ?? 0;
  const signedOutsideAudience = coverage.parents.signedOutsideAudience?.length ?? 0;
  const sentTo = coverageAudienceLabel(coverage.audience);
  const versionLabel = register?.version
    ? `version ${register.version.versionNumber} (${register.version.effectiveDate})`
    : '';

  return (
    <div
      data-testid="policy-responses"
      className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
    >
      <div data-testid="policy-responses-print-header" className="mb-3 hidden print:block">
        <p className="text-base font-semibold">
          Policy responses — {view === 'signed' ? 'Signed' : 'Still to confirm'}
        </p>
        <p className="text-xs text-gray-600">
          {versionLabel} · printed {formatDublinDateTime(new Date().toISOString())}
        </p>
        {sentTo && <p className="text-xs text-gray-600">Sent to: {sentTo}</p>}
      </div>
      <h2 className="text-sm font-semibold text-gray-900">Responses</h2>
      {sentTo && (
        <p
          data-testid="policy-responses-audience"
          className="mt-1 text-xs text-gray-500 print:hidden"
        >
          Sent to: {sentTo}
        </p>
      )}
      <p data-testid="policy-responses-parents" className="mt-2 text-sm text-gray-700">
        {coverage.parents.done} of {coverage.parents.total} children signed
      </p>
      <p data-testid="policy-responses-staff" className="text-sm text-gray-700">
        {coverage.staff.done} of {coverage.staff.total} staff confirmed
      </p>
      {view === 'signed' && departedSigned > 0 && (
        <p data-testid="policy-responses-footnote" className="mt-1 text-xs text-gray-500">
          Also signed: {departedSigned} {departedSigned === 1 ? 'child' : 'children'} who have since
          left the service — included in the Signed list.
        </p>
      )}
      {view === 'signed' && signedOutsideAudience > 0 && (
        <p data-testid="policy-responses-outside-audience" className="mt-1 text-xs text-gray-500">
          {signedOutsideAudience} signed who are no longer in the chosen classes
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 print:hidden">
        <button
          type="button"
          data-testid="policy-responses-view-signed"
          onClick={() => setView('signed')}
          className={`rounded-md px-3 py-1.5 text-sm ${
            view === 'signed' ? 'bg-primary-600 text-white' : 'border border-gray-300 text-gray-700'
          }`}
        >
          Signed ({register?.total ?? coverage.parents.done})
        </button>
        <button
          type="button"
          data-testid="policy-responses-view-outstanding"
          onClick={() => setView('outstanding')}
          disabled={!isCurrentVersion}
          title={
            isCurrentVersion ? undefined : 'Only the current version has children still to confirm'
          }
          className={`rounded-md px-3 py-1.5 text-sm disabled:opacity-50 ${
            view === 'outstanding'
              ? 'bg-primary-600 text-white'
              : 'border border-gray-300 text-gray-700'
          }`}
        >
          Still to confirm ({outstandingCount})
        </button>
        {register && register.versions.length > 1 && (
          <select
            data-testid="policy-responses-version"
            aria-label="Policy version"
            value={versionId ?? register.version?.versionId ?? ''}
            onChange={(e) => setVersionId(e.target.value)}
            className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          >
            {register.versions.map((v) => (
              <option key={v.versionId} value={v.versionId}>
                Version {v.versionNumber} · {v.effectiveDate}
              </option>
            ))}
          </select>
        )}
        <input
          type="search"
          data-testid="policy-responses-search"
          aria-label="Search responses by name"
          placeholder="Search by name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="ml-auto rounded-md border border-gray-300 px-2 py-1.5 text-sm"
        />
        <button
          type="button"
          data-testid="policy-responses-download"
          onClick={handleDownload}
          disabled={exporting}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Download CSV
        </button>
        <button
          type="button"
          data-testid="policy-responses-print"
          onClick={() => window.print()}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          Print
        </button>
      </div>

      <p data-testid="policy-responses-count" className="mt-3 text-xs text-gray-500 print:hidden">
        {isLoading && view === 'signed' ? 'Loading…' : `Showing ${shown} of ${total}`}
      </p>

      {view === 'signed' ? (
        <table className="mt-2 w-full text-sm">
          <thead className="text-left text-xs text-gray-500">
            <tr>
              <th className="py-1 pr-2">Child</th>
              <th className="py-1 pr-2">Signed by</th>
              <th className="py-1 pr-2">Name as typed</th>
              <th className="py-1 pr-2">When</th>
            </tr>
          </thead>
          <tbody>
            {allSignedRows.map((r: PolicySignatureRow) => {
              const isShown = shownSignedIds.has(r.ackId);
              return (
                <tr
                  key={r.ackId}
                  data-testid={`policy-signature-row-${r.ackId}`}
                  data-shown={isShown ? 'true' : 'false'}
                  className={
                    isShown
                      ? 'border-t border-gray-100'
                      : 'hidden border-t border-gray-100 print:table-row'
                  }
                >
                  <td className="py-1 pr-2">
                    {r.audience === 'STAFF' ? (
                      <span className="text-gray-500">Staff</span>
                    ) : (
                      r.childName
                    )}
                    {r.childStatus === 'LEFT' && (
                      <span
                        data-testid={`policy-signature-left-${r.ackId}`}
                        className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                      >
                        Left the service
                      </span>
                    )}
                  </td>
                  <td className="py-1 pr-2">{r.userName}</td>
                  <td className="py-1 pr-2">
                    {r.ackType === 'SIGN' ? (
                      r.signedName
                    ) : (
                      <span className="text-gray-500">Confirmed</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap py-1 pr-2">
                    {formatDublinDateTime(r.acknowledgedAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <ul className="mt-2 space-y-1">
          {allOutstandingRows.map((c) => {
            const isShown = shownOutstandingIds.has(c.childId);
            return (
              <li
                key={c.childId}
                data-testid={`policy-outstanding-row-${c.childId}`}
                data-shown={isShown ? 'true' : 'false'}
                className={
                  isShown ? 'text-sm text-gray-700' : 'hidden text-sm text-gray-700 print:list-item'
                }
              >
                {c.childName}
              </li>
            );
          })}
        </ul>
      )}

      {shown < total && (
        <button
          type="button"
          data-testid="policy-responses-show-more"
          onClick={() => setVisible((n) => n + BATCH_SIZE)}
          className="mt-2 text-sm text-primary-600 underline print:hidden"
        >
          Show {Math.min(BATCH_SIZE, total - shown)} more
        </button>
      )}

      {outstandingCount > 0 && (
        <button
          type="button"
          data-testid="policy-nudge"
          disabled={nudging}
          onClick={handleNudge}
          className="mt-3 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 print:hidden"
        >
          Send a reminder to {outstandingCount} {outstandingCount === 1 ? 'family' : 'families'}
        </button>
      )}
      {nudgeMessage && (
        <p data-testid="policy-nudge-result" className="mt-2 text-xs text-gray-600">
          {nudgeMessage}
        </p>
      )}
    </div>
  );
};

export default PolicyResponses;
